/**
 * "Return after auth": a signed-out visitor on a public page (a tech URL, a
 * university page) opens the sign-in / sign-up modal; once signed in they go
 * back to THAT page, in its signed-in state (owner, 21 + 23 Oct 2026).
 *
 * A brand-new account first gets onboarding in the app at "/" (onboarding
 * lives there, in CosmosApp) and goes back when it finishes or is skipped;
 * an existing account goes straight back.
 *
 * The page is carried two ways, so it survives a Google / LinkedIn round trip
 * through Clerk and /sso-callback:
 *   - sessionStorage (tab-scoped, 30 minutes, used once) — the source of
 *     truth, which also says whether the account is new and how they signed in;
 *   - a ?next=<path> param on /sso-callback and "/".
 * Only a same-site path is ever accepted: it must start with one "/", carry no
 * scheme, host, backslash or control character, and resolve to this origin.
 *
 * Arming (review 24 Oct 2026): a stored page is only ever honoured for the
 * auth attempt that a public page's modal actually started.
 *   - LoginModal / SignUpModal take `returnAfterAuth`; only PublicShell (and
 *     the "/" landing when it resumes a Google sign-up) passes it. Opened
 *     anywhere else, the modals forget any pending page, and Google /
 *     LinkedIn get the plain /sso-callback and "/".
 *   - ARM_KEY: a one-shot marker written when such a modal starts the auth
 *     (the OAuth redirect, or the hand-off to "/" for onboarding). The app
 *     at "/" consumes it on the first signed-in transition
 *     (resolveAuthReturn); no matching marker, no return.
 *   - SSO_FWD_KEY: a one-shot flag set where /sso-callback forwards to
 *     "/?next=". A bare ?next= (no stored page — storage was lost) is only
 *     honoured right after that; otherwise it is ignored. Either way the
 *     param is stripped from the address once decided, so a reload of "/"
 *     never bounces anyone out of the app.
 *
 * Pure: no imports, every function takes its clock / storage, so tests can
 * drive it (see the tu/ unit test). Navigation + tracking live in
 * utils/returnAfterAuthNav.ts.
 */

export const RETURN_KEY = 'utech_return_after_auth';
export const RETURN_TTL_MS = 30 * 60 * 1000;
export const NEXT_PARAM = 'next';
export const ARM_KEY = 'utech_return_armed';
export const SSO_FWD_KEY = 'utech_return_sso_fwd';
const MAX_LEN = 1000;
/** Paths that are not a page to come back to: the app itself, the OAuth hop. */
const NOT_A_RETURN = /^\/(?:sso-callback(?:[/?#]|$)|admin(?:[/?#]|$)|[?#]|$)/i;
const BASE = 'https://return.invalid';

export type ReturnVia = 'password' | 'otp' | 'otp_signup' | 'google' | 'linkedin' | 'sso' | 'login' | 'next_param';

export interface ReturnEntry {
  /** Same-site path + query (+ hash), e.g. /tech/iit-bombay/some-title */
  path: string;
  /** What opened the modal (techpage source, e.g. locked_summary). */
  source: string;
  at: number;
  /** Set by the flow that created the account: onboarding runs first. */
  newAccount: boolean;
  via: ReturnVia | '';
}

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const defaultStore = (): Store | null => {
  try { return typeof sessionStorage !== 'undefined' ? sessionStorage : null; } catch { return null; }
};

/** The validated same-site path, or null. `next` params are dropped from it
 *  (no loops), and "/" / the OAuth callback are not pages to return to. */
export function safeReturnPath(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const p = raw.trim();
  if (!p || p.length > MAX_LEN) return null;
  if (p[0] !== '/' || p[1] === '/' || p[1] === '\\') return null;
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f\u007f\s]/.test(p)) return null;
  let u: URL;
  try { u = new URL(p, BASE); } catch { return null; }
  if (u.origin !== BASE) return null;
  u.searchParams.delete(NEXT_PARAM);
  const out = u.pathname + (u.searchParams.toString() ? `?${u.searchParams.toString()}` : '') + u.hash;
  if (!out.startsWith('/') || out.startsWith('//')) return null;
  if (NOT_A_RETURN.test(out)) return null;
  return out;
}

/** Remember `path` for after sign-in (and forget any older one). Returns the
 *  stored path, or null when it is not a page to return to. */
export function setReturnAfterAuth(path: unknown, source: string, now = Date.now(),
                                   store: Store | null = defaultStore()): string | null {
  const safe = safeReturnPath(path);
  if (!store) return safe;
  try {
    store.removeItem(ARM_KEY);
    if (!safe) { store.removeItem(RETURN_KEY); return null; }
    const e: ReturnEntry = { path: safe, source: String(source || '').slice(0, 80), at: now, newAccount: false, via: '' };
    store.setItem(RETURN_KEY, JSON.stringify(e));
  } catch { /* storage off: the ?next param still carries it */ }
  return safe;
}

/** The stored entry while valid; an expired or malformed one is removed. */
export function readReturnAfterAuth(now = Date.now(), store: Store | null = defaultStore()): ReturnEntry | null {
  if (!store) return null;
  try {
    const raw = store.getItem(RETURN_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    const path = safeReturnPath(v?.path);
    const at = Number(v?.at || 0);
    if (!path || !(at > 0) || now - at > RETURN_TTL_MS || at - now > 60_000) {
      store.removeItem(RETURN_KEY);
      return null;
    }
    return {
      path, at,
      source: typeof v.source === 'string' ? v.source.slice(0, 80) : '',
      newAccount: v.newAccount === true,
      via: typeof v.via === 'string' ? (v.via.slice(0, 20) as ReturnVia) : '',
    };
  } catch {
    try { store.removeItem(RETURN_KEY); } catch { /* ignore */ }
    return null;
  }
}

/** Mark the pending entry (a new account, how they signed in). No-op when none. */
export function updateReturnAfterAuth(patch: Partial<Pick<ReturnEntry, 'newAccount' | 'via'>>,
                                      now = Date.now(), store: Store | null = defaultStore()): ReturnEntry | null {
  const e = readReturnAfterAuth(now, store);
  if (!e || !store) return e;
  const next = { ...e, ...patch };
  try { store.setItem(RETURN_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  return next;
}

/** Forget the pending page, its arming and any SSO forward flag. */
export function clearReturnAfterAuth(store: Store | null = defaultStore()): void {
  try { store?.removeItem(RETURN_KEY); } catch { /* ignore */ }
  try { store?.removeItem(ARM_KEY); } catch { /* ignore */ }
  try { store?.removeItem(SSO_FWD_KEY); } catch { /* ignore */ }
}

/** Arm the pending page for the auth attempt starting now (one-shot).
 *  Returns the entry, or null when there is none to arm. */
export function armReturnAfterAuth(now = Date.now(), store: Store | null = defaultStore()): ReturnEntry | null {
  const e = readReturnAfterAuth(now, store);
  if (!e || !store) return null;
  try { store.setItem(ARM_KEY, JSON.stringify({ path: e.path, at: now })); } catch { return null; }
  return e;
}

/** Take (and remove) a one-shot marker {at, path?}; null when absent or stale. */
function takeOneShot(key: string, now: number, store: Store | null): { path: string | null } | null {
  if (!store) return null;
  try {
    const raw = store.getItem(key);
    if (!raw) return null;
    store.removeItem(key);
    const v = JSON.parse(raw);
    const at = Number(v?.at || 0);
    if (!(at > 0) || now - at > RETURN_TTL_MS || at - now > 60_000) return null;
    return { path: safeReturnPath(v?.path) };
  } catch {
    try { store.removeItem(key); } catch { /* ignore */ }
    return null;
  }
}

/** Where /sso-callback forwards to "/?next=": allow the bare param once. */
export function markSsoForward(now = Date.now(), store: Store | null = defaultStore()): void {
  try { store?.setItem(SSO_FWD_KEY, JSON.stringify({ at: now })); } catch { /* ignore */ }
}

/** `search` without its next= param ('' or '?…'). */
export function stripNextParam(search: string): string {
  try {
    const sp = new URLSearchParams(search || '');
    if (!sp.has(NEXT_PARAM)) return search || '';
    sp.delete(NEXT_PARAM);
    const q = sp.toString();
    return q ? `?${q}` : '';
  } catch { return search || ''; }
}

/** The decision for THIS sign-in, made once on the first signed-in
 *  transition at "/" (MainWithClerk): consumes the arming marker and the
 *  SSO forward flag. The stored page counts only when it was armed for this
 *  attempt (same path); a bare ?next= only right after /sso-callback. An
 *  unarmed stored page is forgotten. The caller strips ?next= either way. */
export function resolveAuthReturn(search: string, now = Date.now(),
                                  store: Store | null = defaultStore()): ReturnEntry | null {
  const arm = takeOneShot(ARM_KEY, now, store);
  const fwd = takeOneShot(SSO_FWD_KEY, now, store);
  const e = readReturnAfterAuth(now, store);
  if (e) {
    if (arm && arm.path === e.path) return e;
    clearReturnAfterAuth(store);
    return null;
  }
  const p = nextFromSearch(search);
  return p && fwd ? { path: p, source: 'next_param', at: now, newAccount: false, via: 'next_param' } : null;
}

let resolved: ReturnEntry | null = null;
/** What resolveAuthReturn decided on this page load, for App (a new
 *  account's return after onboarding). In memory only. */
export function setResolvedReturn(e: ReturnEntry | null): void { resolved = e; }
export function getResolvedReturn(): ReturnEntry | null { return resolved; }

/** ?next=<path> from a query string, validated. */
export function nextFromSearch(search: string): string | null {
  try { return safeReturnPath(new URLSearchParams(search || '').get(NEXT_PARAM)); } catch { return null; }
}

/** Where to go after this sign-in, or null. The stored entry wins (it is
 *  ours, and knows new vs existing); a bare ?next= with no entry (storage
 *  blocked) counts as an existing account. */
export function pendingReturn(search: string, now = Date.now(),
                              store: Store | null = defaultStore()): ReturnEntry | null {
  const e = readReturnAfterAuth(now, store);
  if (e) return e;
  const p = nextFromSearch(search);
  return p ? { path: p, source: 'next_param', at: now, newAccount: false, via: 'next_param' } : null;
}

/** `base` with ?next=<path> (base is a same-site path like "/" or "/sso-callback"). */
export function withNext(base: string, path: string | null | undefined): string {
  const safe = safeReturnPath(path);
  if (!safe) return base;
  return `${base}${base.includes('?') ? '&' : '?'}${NEXT_PARAM}=${encodeURIComponent(safe)}`;
}

/** Clerk's authenticateWithRedirect URLs. `useReturn` (the modal's
 *  returnAfterAuth prop — a public page's modal): /sso-callback and "/" carry
 *  ?next=<page>, the entry records the provider and is armed for this
 *  attempt. Otherwise any pending page is forgotten and the URLs are plain. */
export function ssoRedirectUrls(via: 'google' | 'linkedin', useReturn = false, now = Date.now(),
                                store: Store | null = defaultStore()): { redirectUrl: string; redirectUrlComplete: string } {
  if (!useReturn) {
    clearReturnAfterAuth(store);
    return { redirectUrl: '/sso-callback', redirectUrlComplete: '/' };
  }
  updateReturnAfterAuth({ via }, now, store);
  const e = armReturnAfterAuth(now, store);
  return {
    redirectUrl: withNext('/sso-callback', e?.path),
    redirectUrlComplete: withNext('/', e?.path),
  };
}

/** The absolute URL for `path` on `origin`, or null if it would leave it. */
export function returnHref(path: string, origin: string): string | null {
  const safe = safeReturnPath(path);
  if (!safe || !origin) return null;
  try {
    const u = new URL(safe, origin);
    return u.origin === new URL(origin).origin ? u.href : null;
  } catch { return null; }
}
